const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class StepCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.assembly-steps__track');
    this.slides = Array.from(this.querySelectorAll('.assembly-steps__slide'));
    this.dots = Array.from(this.querySelectorAll('.assembly-steps__dot'));
    if (!this.track || this.slides.length < 2) return;

    this.index = 0;

    this.querySelector('.assembly-steps__nav--prev')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('.assembly-steps__nav--next')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    // The track is a scroll-snap container, so a swipe moves it without going through go().
    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.syncFromScroll(), 80);
    }, { passive: true });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    });

    this.setupDrag();
    this.setupAutoplay();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }

  /**
   * Touch already swipes natively via scroll-snap, so this covers mouse and pen only —
   * hijacking touch would replace a good native gesture with a worse scripted one.
   */
  setupDrag() {
    let startX = 0;
    let startScroll = 0;
    let moved = 0;
    let dragging = false;

    const onDown = (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragging = true;
      moved = 0;
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
      // Snapping fights a scrollLeft written frame by frame; restore it on release.
      this.track.style.scrollSnapType = 'none';
      this.track.setPointerCapture(event.pointerId);
      this.classList.add('is-dragging');
      this.stop();
    };

    const onMove = (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      moved = Math.abs(delta);
      this.track.scrollLeft = startScroll - delta;
      if (moved > 5) event.preventDefault();
    };

    const onUp = (event) => {
      if (!dragging) return;
      dragging = false;
      this.track.releasePointerCapture?.(event.pointerId);
      this.track.style.scrollSnapType = '';
      this.classList.remove('is-dragging');

      // A short drag reads as a flick: advance one slide in that direction.
      const delta = event.clientX - startX;
      if (moved > 40) {
        this.go(this.index + (delta < 0 ? 1 : -1));
      } else {
        this.go(Math.round(this.track.scrollLeft / this.track.clientWidth));
      }
      this.start();
    };

    this.track.addEventListener('pointerdown', onDown);
    this.track.addEventListener('pointermove', onMove);
    this.track.addEventListener('pointerup', onUp);
    this.track.addEventListener('pointercancel', onUp);
    // A drag that ends over a slide must not follow the link underneath it.
    this.track.addEventListener('click', (event) => {
      if (moved > 5) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  setupAutoplay() {
    const seconds = Number(this.dataset.autoplay);
    if (!seconds || REDUCED.matches) return;
    this.interval = seconds * 1000;

    // Only run while the step is on screen — thirteen of these off-screen is wasted work.
    this.observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.start() : this.stop()),
      { threshold: 0.4 }
    );
    this.observer.observe(this);

    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
  }

  start() {
    if (this.timer || !this.interval) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  syncFromScroll() {
    const nearest = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (nearest !== this.index) this.setIndex(nearest);
  }

  setIndex(next) {
    this.index = (next + this.slides.length) % this.slides.length;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === this.index)));
  }

  go(next) {
    this.setIndex(next);
    this.track.scrollTo({
      left: this.track.clientWidth * this.index,
      behavior: REDUCED.matches ? 'auto' : 'smooth',
    });
  }
}

customElements.define('step-carousel', StepCarousel);
